import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import { Apiary, ForageMarker, BeeBaseAlert } from '../../types';
import { useBeeBaseAlerts } from '../beebase/useBeeBaseAlerts';
import { useBeeBaseRisk, RiskMatch } from '../beebase/useBeeBaseRisk';
import { notifyUser, showInAppNotification, requestNotificationPermission } from '../notifications/notifyUser';
import 'leaflet/dist/leaflet.css';
import './ApiariesMap.css';

// Fix for default markers in React Leaflet
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Simple forage marker icon
const ForageIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41">
      <path d="M12.5,0C5.6,0,0,5.6,0,12.5c0,8.1,11.8,27.5,12,27.9c0.1,0.2,0.3,0.4,0.5,0.4s0.4-0.1,0.5-0.4
        c0.2-0.4,12-19.8,12-27.9C25,5.6,19.4,0,12.5,0z M12.5,19c-3.6,0-6.5-2.9-6.5-6.5s2.9-6.5,6.5-6.5s6.5,2.9,6.5,6.5
        S16.1,19,12.5,19z" fill="#ff69b4"/>
      <circle cx="12.5" cy="12.5" r="4" fill="white"/>
      <text x="12.5" y="16" text-anchor="middle" font-size="10" fill="#ff69b4" font-weight="bold">F</text>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// BeeBase Alert Icons - Color-coded by disease type
const getBeeBaseAlertIcon = (disease: string) => {
  const diseaseColors = {
    'AFB': '#ff0000', // Red
    'American Foulbrood': '#ff0000',
    'EFB': '#ff8800', // Orange  
    'European Foulbrood': '#ff8800',
    'Tropilaelaps': '#0066ff', // Blue
    'Small Hive Beetle': '#00aa00', // Green
    'Varroa': '#8800ff', // Purple
    'Varroa destructor': '#8800ff', // Purple
    'Nosema': '#ffaa00', // Yellow
    'Chalkbrood': '#ff6699', // Pink
    'default': '#ff6600' // Default orange
  };
  
  const color = diseaseColors[disease as keyof typeof diseaseColors] || diseaseColors.default;
  const shortName = disease.substring(0, 3).toUpperCase();
  
  return L.icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41">
        <path d="M12.5,0C5.6,0,0,5.6,0,12.5c0,8.1,11.8,27.5,12,27.9c0.1,0.2,0.3,0.4,0.5,0.4s0.4-0.1,0.5-0.4
          c0.2-0.4,12-19.8,12-27.9C25,5.6,19.4,0,12.5,0z M12.5,19c-3.6,0-6.5-2.9-6.5-6.5s2.9-6.5,6.5-6.5s6.5,2.9,6.5,6.5
          S16.1,19,12.5,19z" fill="${color}"/>
        <circle cx="12.5" cy="12.5" r="4" fill="white"/>
        <text x="12.5" y="16" text-anchor="middle" font-size="7" fill="${color}" font-weight="bold">!</text>
      </svg>
    `),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    className: `beebase-alert-marker alert-${disease.toLowerCase().replace(/\s+/g, '-')}`
  });
};

// Highlighted apiary icon for selected apiary
const HighlightedApiaryIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41">
      <path d="M12.5,0C5.6,0,0,5.6,0,12.5c0,8.1,11.8,27.5,12,27.9c0.1,0.2,0.3,0.4,0.5,0.4s0.4-0.1,0.5-0.4
        c0.2-0.4,12-19.8,12-27.9C25,5.6,19.4,0,12.5,0z M12.5,19c-3.6,0-6.5-2.9-6.5-6.5s2.9-6.5,6.5-6.5s6.5,2.9,6.5,6.5
        S16.1,19,12.5,19z" fill="#ff6600" stroke="#fff" stroke-width="2"/>
      <circle cx="12.5" cy="12.5" r="4" fill="white"/>
      <text x="12.5" y="16" text-anchor="middle" font-size="8" fill="#ff6600" font-weight="bold">H</text>
    </svg>
  `),
  iconSize: [30, 46],
  iconAnchor: [15, 46],
  popupAnchor: [1, -40],
  className: 'highlighted-apiary-marker'
});

interface ApiariesMapProps {
  apiaries: Apiary[];
  forageMarkers: ForageMarker[];
  selectedApiaryId?: string;
  onApiaryClick?: (apiary: Apiary) => void;
  onForageMarkerClick?: (marker: ForageMarker) => void;
  onMapClick?: (lat: number, lng: number) => void;
  onAddForageMarker?: (lat: number, lng: number) => Promise<void>;
  onEditForageMarker?: (marker: ForageMarker) => void;
  onInspectApiary?: (apiary: Apiary) => void;
  showForageMarkers?: boolean;
  showBeeBaseAlerts?: boolean;
  showRiskAssessment?: boolean;
}

interface BeeBaseFilters {
  disease?: string;
  startDate?: string;
  endDate?: string;
}

const ApiariesMap: React.FC<ApiariesMapProps> = ({ 
  apiaries, 
  forageMarkers, 
  selectedApiaryId,
  onApiaryClick, 
  onForageMarkerClick,
  onMapClick,
  onAddForageMarker,
  onEditForageMarker,
  onInspectApiary,
  showForageMarkers = true,
  showBeeBaseAlerts = true,
  showRiskAssessment = true
}) => {
  const [filters, setFilters] = useState<BeeBaseFilters>({});
  const [riskRadius, setRiskRadius] = useState(10); // km
  const [showRiskLines, setShowRiskLines] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notifiedAlerts, setNotifiedAlerts] = useState<Set<string>>(new Set());
  const [alertsCollapsed, setAlertsCollapsed] = useState(true); // Start minimized by default
  
  const { alerts, loading: alertsLoading } = useBeeBaseAlerts(filters);
  const riskMatches = useBeeBaseRisk(apiaries, alerts, riskRadius);

  // Request notification permission on component mount
  useEffect(() => {
    const checkNotificationPermission = async () => {
      const granted = await requestNotificationPermission();
      setNotificationsEnabled(granted);
    };
    checkNotificationPermission();
  }, []);

  // Handle risk notifications
  useEffect(() => {
    if (!notificationsEnabled || riskMatches.length === 0) return;

    riskMatches.forEach(match => {
      const alertKey = `${match.alertId}-${match.apiaryId}`;
      
      // Only notify once per alert-apiary combination
      if (notifiedAlerts.has(alertKey)) return;

      if (match.risk === 'High') {
        notifyUser(
          `🚨 High Risk Alert: ${match.disease}`,
          `${match.apiaryName} is ${match.distanceKm}km from an outbreak`
        );
        showInAppNotification(
          'High Risk Disease Alert',
          `${match.disease} detected ${match.distanceKm}km from ${match.apiaryName}. Immediate inspection recommended.`,
          'error'
        );
        setNotifiedAlerts(prev => new Set(prev).add(alertKey));
      } else if (match.risk === 'Medium') {
        showInAppNotification(
          'Medium Risk Disease Alert',
          `${match.disease} detected ${match.distanceKm}km from ${match.apiaryName}. Monitor closely.`,
          'warning'
        );
        setNotifiedAlerts(prev => new Set(prev).add(alertKey));
      }
    });
  }, [riskMatches, notificationsEnabled, notifiedAlerts]);

  // Get unique disease types for filter options
  const uniqueDiseases = Array.from(new Set(alerts.map(alert => alert.disease).filter(Boolean))) as string[];

  const handleDiseaseFilterChange = (disease: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      disease: checked ? disease : undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  // All alerts are already filtered by the hook
  const visibleAlerts = alerts;
  // Default center to UK if no apiaries
  const defaultCenter: LatLngExpression = [54.5, -2]; // Center of UK
  
  // Focus on selected apiary if available, otherwise use first apiary
  const selectedApiary = selectedApiaryId ? apiaries.find(a => a.id === selectedApiaryId) : null;
  const centerApiary = selectedApiary || (apiaries.length > 0 ? apiaries[0] : null);
  const center = centerApiary ? [centerApiary.lat, centerApiary.lng] as LatLngExpression : defaultCenter;
  const zoom = centerApiary ? 14 : 6; // Higher zoom for better focus on specific apiary

  console.log('Rendering simple map:', { center, zoom, apiariesCount: apiaries.length, forageCount: forageMarkers.length, selectedApiaryId, alertsCollapsed });

  // Handle map click for adding forage markers
  const MapClickHandler: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      const handleClick = async (e: any) => {
        const { lat, lng } = e.latlng;
        console.log('Map clicked at:', lat, lng);
        
        // Try onAddForageMarker first (new prop), then fallback to onMapClick (legacy)
        if (onAddForageMarker) {
          await onAddForageMarker(lat, lng);
        } else if (onMapClick) {
          onMapClick(lat, lng);
        }
      };

      map.on('click', handleClick);
      return () => {
        map.off('click', handleClick);
      };
    }, [map]);

    return null;
  };

  // Component to handle map view changes when apiary selection changes
  const MapViewController: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      if (centerApiary) {
        // Smooth fly to the selected apiary
        map.flyTo([centerApiary.lat, centerApiary.lng], 14, {
          duration: 1.5, // 1.5 seconds animation
          easeLinearity: 0.25
        });
      }
    }, [map, centerApiary]);

    return null;
  };

  return (
    <div style={{ height: '750px', width: '100%' }}>
      <h3 style={{ marginBottom: '15px' }}>
        Map: Apiaries: {apiaries.length} | Forage: {forageMarkers.length} | BeeBase Alerts: {visibleAlerts.length} | Risk Matches: {riskMatches.length} | Zoom: {zoom}
      </h3>
      <p style={{ marginBottom: '15px' }}>
        <strong>💡 Tip:</strong> Click anywhere on the map to add a forage source at that location!
      </p>
      
      {/* Risk Assessment Controls */}
      {showRiskAssessment && apiaries.length > 0 && (
        <div className="risk-controls" style={{ 
          padding: '10px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '5px', 
          marginBottom: '10px',
          border: '1px solid #ffeaa7'
        }}>
          <h4 style={{ margin: '0 0 10px 0' }}>⚠️ Risk Assessment</h4>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <label>Risk Radius (km):</label>
              <input 
                type="number" 
                value={riskRadius} 
                onChange={e => setRiskRadius(parseFloat(e.target.value) || 10)} 
                min={1} 
                max={50}
                style={{ width: '60px', padding: '2px 5px' }}
              />
            </div>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <input
                type="checkbox"
                checked={showRiskLines}
                onChange={e => setShowRiskLines(e.target.checked)}
              />
              Show Risk Lines
            </label>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>Notifications:</span>
              <span style={{ 
                color: notificationsEnabled ? 'green' : 'red',
                fontWeight: 'bold'
              }}>
                {notificationsEnabled ? '✓ Enabled' : '✗ Disabled'}
              </span>
            </div>
          </div>
          
          {riskMatches.length > 0 && (
            <div style={{ 
              padding: '8px', 
              backgroundColor: '#fff', 
              borderRadius: '3px',
              border: '1px solid #e0e0e0'
            }}>
              <strong>Risk Summary:</strong>
              <div style={{ display: 'flex', gap: '15px', marginTop: '5px', fontSize: '0.9em' }}>
                <span style={{ color: '#dc3545' }}>
                  High: {riskMatches.filter(m => m.risk === 'High').length}
                </span>
                <span style={{ color: '#fd7e14' }}>
                  Medium: {riskMatches.filter(m => m.risk === 'Medium').length}
                </span>
                <span style={{ color: '#ffc107' }}>
                  Low: {riskMatches.filter(m => m.risk === 'Low').length}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* BeeBase Alert Controls */}
      {showBeeBaseAlerts && (
        <div className="beebase-controls" style={{ 
          padding: '15px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '5px', 
          marginBottom: '10px',
          border: '1px solid #ddd',
          maxHeight: alertsCollapsed ? '60px' : '400px',
          overflow: alertsCollapsed ? 'hidden' : 'auto',
          transition: 'max-height 0.3s ease'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: alertsCollapsed ? '0' : '15px'
          }}>
            <h4 style={{ margin: '0', fontSize: '1.1em', color: '#333' }}>
              🚨 BeeBase Disease Alerts {alertsLoading && '(Loading...)'}
            </h4>
            <button
              onClick={() => setAlertsCollapsed(!alertsCollapsed)}
              style={{
                backgroundColor: '#007cba',
                color: 'white',
                border: 'none',
                fontSize: '1em',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '4px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              title={alertsCollapsed ? 'Expand alerts' : 'Minimize alerts'}
            >
              {alertsCollapsed ? '⬆️ Expand' : '⬇️ Minimize'}
            </button>
          </div>
          
          {!alertsCollapsed && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ marginRight: '10px' }}>
                  <strong>Filter by Disease:</strong>
                </div>
                
                {uniqueDiseases.map(disease => (
                  <label key={disease} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                      type="radio"
                      name="disease"
                      checked={filters.disease === disease}
                      onChange={(e) => handleDiseaseFilterChange(disease, e.target.checked)}
                    />
                    <span className={`disease-label disease-${disease.toLowerCase().replace(/\s+/g, '-')}`}>
                      {disease}
                    </span>
                  </label>
                ))}
                
                <button 
                  onClick={clearFilters}
                  style={{ 
                    padding: '5px 10px', 
                    backgroundColor: '#007cba', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '3px',
                    cursor: 'pointer'
                  }}
                >
                  Show All
                </button>
              </div>
              
              <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                Showing {visibleAlerts.length} alerts
                {filters.disease && ` (filtered by ${filters.disease})`}
              </div>
              
              {/* Legend */}
              <div style={{ 
                marginTop: '15px', 
                padding: '10px', 
                backgroundColor: '#fff', 
                borderRadius: '5px',
                border: '1px solid #e0e0e0'
              }}>
                <strong style={{ fontSize: '0.9em', color: '#666', display: 'block', marginBottom: '8px' }}>Legend:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#ff0000', borderRadius: '50%' }}></div>
                    AFB (Red)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#ff8800', borderRadius: '50%' }}></div>
                    EFB (Orange)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#0066ff', borderRadius: '50%' }}></div>
                    Tropilaelaps (Blue)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#00aa00', borderRadius: '50%' }}></div>
                    Small Hive Beetle (Green)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#8800ff', borderRadius: '50%' }}></div>
                    Varroa (Purple)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#ffaa00', borderRadius: '50%' }}></div>
                    Nosema (Yellow)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#ff6699', borderRadius: '50%' }}></div>
                    Chalkbrood (Pink)
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={true}
        style={{ 
          height: alertsCollapsed ? '820px' : '500px', 
          width: '100%',
          transition: 'height 0.3s ease'
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapClickHandler />
        <MapViewController />
        
        {/* Simple markers for apiaries */}
        {apiaries.map((apiary) => (
          <Marker 
            key={apiary.id} 
            position={[apiary.lat, apiary.lng]}
            icon={apiary.id === selectedApiaryId ? HighlightedApiaryIcon : DefaultIcon}
            eventHandlers={{
              click: () => {
                if (onApiaryClick) {
                  onApiaryClick(apiary);
                }
              }
            }}
          >
            <Popup>
              <div>
                <h4>🏡 {apiary.name}</h4>
                <p>{apiary.location}</p>
                <p><small>Coordinates: {apiary.lat.toFixed(4)}, {apiary.lng.toFixed(4)}</small></p>
                
                {/* Action Buttons */}
                <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {onInspectApiary && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onInspectApiary(apiary);
                      }}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '0.8em'
                      }}
                    >
                      🔍 Inspect
                    </button>
                  )}
                  
                  {onApiaryClick && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onApiaryClick(apiary);
                      }}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#007cba',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '0.8em'
                      }}
                    >
                      📋 Details
                    </button>
                  )}
                </div>
                
                {/* Risk Status */}
                {riskMatches.filter(match => match.apiaryId === apiary.id).length > 0 && (
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '5px', 
                    backgroundColor: '#fff3cd',
                    borderRadius: '3px',
                    fontSize: '0.8em'
                  }}>
                    ⚠️ <strong>{riskMatches.filter(match => match.apiaryId === apiary.id).length} risk alert(s)</strong>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        
        {/* Forage markers */}
        {showForageMarkers && forageMarkers.map((marker) => (
          <Marker key={marker.id} position={[marker.lat, marker.lng]} icon={ForageIcon}>
            <Popup>
              <div>
                <h4>🌸 Forage Source</h4>
                <p><strong>Type:</strong> {marker.type.replace('_', ' ')}</p>
                <p><strong>Season:</strong> {marker.season.replace('_', ' ')}</p>
                {marker.notes && <p><strong>Notes:</strong> {marker.notes}</p>}
                <p><small>Added: {new Date(marker.createdAt).toLocaleDateString()}</small></p>
                <p><small>Coordinates: {marker.lat.toFixed(4)}, {marker.lng.toFixed(4)}</small></p>
              </div>
            </Popup>
          </Marker>
        ))}
        
        {/* BeeBase Alert markers */}
        {showBeeBaseAlerts && visibleAlerts.map((alert) => (
          alert.lat && alert.lng && (
            <Marker 
              key={alert.id} 
              position={[alert.lat, alert.lng]} 
              icon={getBeeBaseAlertIcon(alert.disease || 'Unknown')}
            >
              <Popup>
                <div>
                  <h4>🚨 BeeBase Disease Alert</h4>
                  <p><strong>Disease:</strong> {alert.disease || 'Unknown'}</p>
                  {alert.location && <p><strong>Location:</strong> {alert.location}</p>}
                  {alert.date && <p><strong>Date:</strong> {new Date(alert.date).toLocaleDateString()}</p>}
                  {alert.details && <p><strong>Details:</strong> {alert.details}</p>}
                  {alert.notes && <p><strong>Notes:</strong> {alert.notes}</p>}
                  <p><small>Coordinates: {alert.lat.toFixed(4)}, {alert.lng.toFixed(4)}</small></p>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '5px', 
                    backgroundColor: '#ffe6e6', 
                    borderRadius: '3px',
                    fontSize: '0.85em'
                  }}>
                    ⚠️ <strong>Alert:</strong> Monitor nearby hives for signs of this disease
                  </div>
                </div>
              </Popup>
            </Marker>
          )
        ))}
        
        {/* Risk Lines */}
        {showRiskAssessment && showRiskLines && riskMatches.map(match => {
          const alert = alerts.find(a => a.id === match.alertId);
          const apiary = apiaries.find(a => a.id === match.apiaryId);
          if (!alert || !apiary || !alert.lat || !alert.lng) return null;

          const getRiskColor = (risk: string) => {
            switch (risk) {
              case 'High': return '#dc3545';
              case 'Medium': return '#fd7e14';
              case 'Low': return '#ffc107';
              default: return '#6c757d';
            }
          };

          return (
            <Polyline
              key={`riskline-${match.alertId}-${match.apiaryId}`}
              positions={[[apiary.lat, apiary.lng], [alert.lat, alert.lng]]}
              pathOptions={{ 
                color: getRiskColor(match.risk),
                weight: match.risk === 'High' ? 4 : match.risk === 'Medium' ? 3 : 2,
                opacity: 0.7,
                dashArray: match.risk === 'Low' ? '10, 10' : undefined
              }}
            >
              <Popup>
                <div>
                  <h4 style={{ color: getRiskColor(match.risk) }}>
                    {match.risk} Risk Connection
                  </h4>
                  <p><strong>Apiary:</strong> {match.apiaryName}</p>
                  <p><strong>Disease:</strong> {match.disease}</p>
                  <p><strong>Distance:</strong> {match.distanceKm}km</p>
                  {match.location && <p><strong>Alert Location:</strong> {match.location}</p>}
                  {match.alertDate && <p><strong>Alert Date:</strong> {new Date(match.alertDate).toLocaleDateString()}</p>}
                </div>
              </Popup>
            </Polyline>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default ApiariesMap;
